import React, { ReactElement, ReactNode, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Popover } from 'react-native-popper'

import styled, { useTheme } from 'styled-components/native'

import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { getActiveFilterCount } from './activeFilterUtils'
import { Display } from '../../atoms/display/Display'
import { CheckboxGroup } from '../../atoms/formElements/CheckboxGroup'
import { InputField } from '../../atoms/formElements/InputField'
import { RadioGroup } from '../../atoms/formElements/RadioButtons'
import { IconButton } from '../../atoms/iconButton/IconButton'
import { FilterIcon } from '../../atoms/icons/FilterIcon'
import { NumberBadge } from '../../atoms/icons/NumberBadge'
import { DisplaySize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type TextFilterSection = {
  type: 'text'
  label: ReactNode
  placeholder?: string
  value: string
  defaultValue?: string
  onChange: (value: string) => void
  leftIcon?: ReactElement
}

export type RadioFilterSection<T extends string = string> = {
  type: 'radio'
  label: ReactNode
  options: { id: T; text: string }[]
  value: T | null
  defaultValue?: T | null
  onChange: (value: string | null) => void
}

export type CheckboxFilterSection = {
  type: 'checkbox'
  label: ReactNode
  options: { id: string; text: string }[]
  value: string[]
  defaultValue?: string[]
  onChange: (values: string[]) => void
}

export type TaskFilterMenuSectionConfig = TextFilterSection | RadioFilterSection<string> | CheckboxFilterSection

export type TaskFilterMenuProps = {
  sections: TaskFilterMenuSectionConfig[]
}

const Panel = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: '16px',
  boxShadow: `0px 4px 16px ${theme.colors.shadowLow}`,
  padding: theme.spacing['16px'],
  gap: theme.spacing['12px'],
  width: '320px',
}))

const Section = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
}))

const FilterButtonContainer = styled.View({
  position: 'relative',
})

const ActiveFilterBadgeContainer = styled.View({
  position: 'absolute',
  top: 0,
  right: -8,
  zIndex: 1,
  pointerEvents: 'none',
})

export const TaskFilterMenu: React.FC<TaskFilterMenuProps> = ({ sections }) => {
  const ref = useRef(null)
  const { colors } = useTheme()
  const intl = useIntl()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const activeFilterCount = getActiveFilterCount(sections)
  const accessibilityLabel =
    activeFilterCount > 0
      ? intl.formatMessage(
          {
            defaultMessage: 'Filter and sort options, {count, plural, one {# active filter} other {# active filters}}',
            description: 'Accessibility label for the filter menu trigger button when filters are active',
          },
          { count: activeFilterCount },
        )
      : intl.formatMessage({
          defaultMessage: 'Filter and sort options',
          description: 'Accessibility label for the filter menu trigger button',
        })

  return (
    <>
      <Popover
        trigger={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement='bottom left'
        animated={!window.Cypress}
      >
        <Popover.Content>
          <Panel>
            {sections.map((section, index) => (
              <Section key={index}>
                <Display size={DisplaySize.EYEBROW} text={section.label} />
                {section.type === 'text' && (
                  <InputField
                    testID={tID('TaskFilterMenu-search')}
                    value={section.value}
                    onChange={(event) => section.onChange(event.nativeEvent.text)}
                    placeholder={section.placeholder}
                    leftIcon={section.leftIcon}
                    baseInputStyle={{ marginBottom: 0 }}
                  />
                )}
                {section.type === 'radio' && (
                  <RadioGroup
                    buttons={section.options.map((opt) => ({ label: opt.text, value: opt.id }))}
                    selectedValue={section.value}
                    onChange={(value) => section.onChange(value as string)}
                    name={`TaskFilterMenu-radio-${index}`}
                    baseInputStyle={{ marginBottom: 0 }}
                    buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
                    buttonStyle={{ marginBottom: 8 }}
                  />
                )}
                {section.type === 'checkbox' && (
                  <CheckboxGroup
                    options={section.options}
                    value={section.value}
                    onChange={section.onChange}
                    name={`TaskFilterMenu-checkbox-${index}`}
                    baseInputStyle={{ marginBottom: 0 }}
                    checkboxStyle={{ marginRight: 16 }}
                    checkboxInputStyle={{ marginBottom: 8 }}
                  />
                )}
              </Section>
            ))}
          </Panel>
        </Popover.Content>
      </Popover>
      <FilterButtonContainer ref={ref}>
        <IconButton
          testID={tID('TaskFilterMenu-trigger-button')}
          accessibilityLabel={accessibilityLabel}
          leftIcon={<FilterIcon size={24} color={colors.iconDefault} />}
          onPress={() => setIsPopoverOpen((prev) => !prev)}
        />
        {activeFilterCount > 0 && (
          <ActiveFilterBadgeContainer testID={tID('TaskFilterMenu-active-filter-count')}>
            <NumberBadge
              number={activeFilterCount}
              backgroundColor={colors.components.taskFilterMenu.activeFilterBadge.background}
              color={colors.components.taskFilterMenu.activeFilterBadge.text}
            />
          </ActiveFilterBadgeContainer>
        )}
      </FilterButtonContainer>
    </>
  )
}
